import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, refreshSession, getSession, ACCOUNTANT_API_URL, APIRequest, APIResponse, TRADING_API_URL } from '../../../../app/Api';
import { formatDate, timestampToDate } from '../../../../app/Helpers';
import { DynamicField } from '../../../common/DynamicFields/DynamicFields';
import { UpdateOrderTransactionRequest, UpdateOrderTransactionResponse, GetOrderTransactionRequest, GetOrderTransactionResponse, } from '../../../../repository/Trading/orderTransaction_pb';
import { OrderTransactionEndpointClient } from '../../../../repository/Trading/OrderTransactionServiceClientPb';
import { OrderAction } from '../../../../app/Enums';


export interface TradesFormState {
    isLoading: boolean
    message?: ApiMessage
    stage1: any,


}

const initialState: TradesFormState = {
    isLoading: false,
    message: undefined,
    stage1: undefined,


}

const client = new OrderTransactionEndpointClient(TRADING_API_URL, null, null);



export const update = createAsyncThunk<UpdateOrderTransactionResponse.AsObject, APIRequest<UpdateOrderTransactionRequest>, {

    rejectValue: ApiMessage
}>(
    'forms/trading/trades/update',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await client.update(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)



export const get = createAsyncThunk<GetOrderTransactionResponse.AsObject, APIRequest<GetOrderTransactionRequest>, {

    rejectValue: ApiMessage
}>(
    'forms/trading/trades/get',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await client.getOrderTransaction(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)


export const tradesFormSlice = createSlice({
    name: 'forms/trading/trades',
    initialState,
    reducers: {

        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.isLoading = false;
            state.message = undefined;
            state.stage1 = initialState.stage1;


        },
        setStage1: (state, action: PayloadAction<any>) => {
            let stage1 = {
                id: action.payload?.id,
                extraFields: action.payload?.extraFields
            }
            state.stage1 = stage1;
        },

        setMessage: (state, action: PayloadAction<ApiMessage>) => {
            state.message = action.payload;
        },
        resetMessage: (state) => {
            state.message = undefined;
        },
    },
    extraReducers: (builder) => {


        builder.addCase(get.fulfilled, (state, { payload }) => {
            state.isLoading = false
            if (payload?.success) {
                let val = payload.success;
                var res = {
                    id: val.id,
                    currencyId: val.currency?.id?.value ?? "",
                    currencyName: val.currency?.name?.value ?? "",
                    currencyCode: val.currency?.code?.value ?? "",
                    currencyDecimalPlaces: val.currency?.decimalplaces?.value ?? undefined,
                    currencyForm: val.currency?.form?.value ?? undefined,
                    currencySymbol: val.currency?.symbol?.value ?? "",
                    amount: val.amount?.value,
                    statusId: val.status?.id?.value,
                    statusCreatedDate: formatDate(timestampToDate(val.status?.createddate?.seconds, val.status?.createddate?.nanos)),
                    statusCreatedById: val.status?.createdby?.id?.value,
                    statusCreatedBy: (val.status?.createdby?.firstname?.value ?? "") + " " + (val.status?.createdby?.lastname?.value ?? ""),
                    sellOrder: undefined as any,
                    buyOrder: undefined as any,
                    buyTransactionId: undefined as any,
                    buyTransactionInternalId: undefined as any,
                    sellTransactionId: undefined as any,
                    sellTransactionInternalId: undefined as any,
                    clientId: val.client?.id?.value,
                    clientName: (val.client?.firstname?.value ?? "") + " " + (val.client?.lastname?.value ?? ""),
                    createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                    addedBy: (val.createdby?.firstname?.value ?? "") + " " + (val.createdby?.lastname?.value ?? ""),
                    addedById: val.createdby?.id?.value,
                    fromAccountInternalId: val?.fromaccount?.internalid?.value ?? "",
                    fromAccountName: val?.fromaccount?.name?.value ?? "",
                    fromAccountId: val?.fromaccount?.id?.value ?? "",
                    toAccountInternalId: val?.toaccount?.internalid?.value ?? "",
                    toAccountName: val?.toaccount?.name?.value ?? "",
                    toAccountId: val?.toaccount?.id?.value ?? "",

                    extraFields: [] as DynamicField[],

                    itemId: val.tradeditem?.id?.value ?? undefined,
                    currencyPairId: val.tradeditem?.item?.id?.value,
                    currencyPairName: val.tradeditem?.item?.name?.value,
                    currencyPairBaseCurrencyId: val.tradeditem?.item?.base?.id?.value ?? "",
                    currencyPairBaseCurrencyName: val.tradeditem?.item?.base?.name?.value ?? "",
                    currencyPairBaseCurrencyCode: val.tradeditem?.item?.base?.code?.value ?? "",
                    currencyPairBaseCurrencyDecimalPlaces: val.tradeditem?.item?.base?.decimalplaces?.value ?? undefined,
                    currencyPairBaseCurrencyForm: val.tradeditem?.item?.base?.form?.value ?? undefined,
                    currencyPairBaseCurrencySymbol: val.tradeditem?.item?.base?.symbol?.value ?? "",
                    currencyPairQuoteCurrencyId: val.tradeditem?.item?.quote?.id?.value ?? "",
                    currencyPairQuoteCurrencyName: val.tradeditem?.item?.quote?.name?.value ?? "",
                    currencyPairQuoteCurrencyCode: val.tradeditem?.item?.quote?.code?.value ?? "",
                    currencyPairQuoteCurrencyDecimalPlaces: val.tradeditem?.item?.quote?.decimalplaces?.value ?? undefined,
                    currencyPairQuoteCurrencyForm: val.tradeditem?.item?.quote?.form?.value ?? undefined,
                    currencyPairQuoteCurrencySymbol: val.tradeditem?.item?.quote?.symbol?.value ?? "",
                    itemPrice: val.tradeditem?.price?.value,
                    itemQuantity: val.tradeditem?.quantity?.value,
                    itemSubTotal: val.tradeditem?.subtotal?.value,

                    //referenceOrderId: val?.referenceorder?.id?.value,

                }
                if (payload?.success?.extrafieldsList) {
                    let count = 0
                    payload?.success?.extrafieldsList?.forEach(element => {
                        res.extraFields.push({ key: element.key, label: element.label, type: element.type, value: element.value })
                        count++
                    });
                }
                if (val.order?.tradeaction?.value == OrderAction.BUY) {
                    res.buyOrder = val.order?.id?.value;
                    res.sellOrder = val.referenceorder?.id?.value;

                    res.buyTransactionId = val.matchtransaction?.id?.value
                    res.buyTransactionInternalId = val.matchtransaction?.internalid?.value
                    res.sellTransactionId = val.referencetransaction?.id?.value
                    res.sellTransactionInternalId = val.referencetransaction?.internalid?.value

                } else {
                    res.sellOrder = val.order?.id?.value;
                    res.buyOrder = val.referenceorder?.id?.value;


                    res.buyTransactionId = val.referencetransaction?.id?.value
                    res.buyTransactionInternalId = val.referencetransaction?.internalid?.value
                    res.sellTransactionId = val.matchtransaction?.id?.value
                    res.sellTransactionInternalId = val.matchtransaction?.internalid?.value

                }

                state.stage1 = res
            }
        })
        builder.addCase(get.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(get.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })
        builder.addCase(update.fulfilled, (state, { payload }) => {
            if (payload)
                state.message = toApiMessage(payload.success?.message);
            state.isLoading = false;
        })
        builder.addCase(update.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(update.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })
    }
})

export const { reset, dismissMessage, setStage1, setMessage, resetMessage, } = tradesFormSlice.actions

export default tradesFormSlice.reducer


